import React, { useState, useEffect, useContext, useMemo } from "react";
import "./styles.scss";
import Lottie from "react-lottie";
import "react-datepicker/dist/react-datepicker.css";
import DataTable from "react-data-table-component";
import ModalCrud from "../../../Components/ModalCrud";
import { 
    GetAllColaboradores, 
    GetAllFuncoes, 
    GetAllExpediente,
    updateColaboradorById,
    deleteColaboradorById
} from "../../../Services/ApiCalls";
import MainContext from "../../../Contexts/MainContext";
import SelectedColaborador from "../../../Components/RenderSelectedRow/SelectedColaborador";
import AddSelectedColaborador from "../../../Components/RenderSelectedRow/AddSelectedColaborador";
import HeaderInside from "../../../Components/HeaderInside";
import EmptyData from "../../../Components/EmptyData";
import { Link } from "react-router-dom";
import { BsDownload } from "react-icons/bs";
import { FaPen, FaTrash, FaSearch, FaFilter } from "react-icons/fa";
import { showToast } from "../../../Functions";

const LOADING = require("../../../Assets/animations/loading.json");

const Usuarios: React.FC = () => {
    const {
        token,
        openMoreInfo,
        setOpenMoreInfo,
        hasCloseEditModal,
        addModalOpen,
        setaddModalOpen,
    } = useContext(MainContext);

    const [selectedColaborador, setSelectedColaborador] = useState({});
    
    // Filtros Locais
    const [filterText, setFilterText] = useState("");
    const [filterStatus, setFilterStatus] = useState("all");
    const [localRefresh, setLocalRefresh] = useState(false);

    // API Data
    const { dataAllColaboradores, statusCodeAllColaboradores } = GetAllColaboradores(token, hasCloseEditModal || localRefresh);
    const { dataAllFuncoes } = GetAllFuncoes(token);
    const { dataAllExpedientes } = GetAllExpediente(token);

    useEffect(() => {
        document.title = "Marca Ponto - Usuários";
        setOpenMoreInfo(false);
    }, []);

    const closeModal = () => {
        setaddModalOpen(false);
        return true;
    };

    const closeModalMoreInfo = () => {
        setOpenMoreInfo(false);
        return true;
    };

    const showMoreInfo = (dataFromRow: any) => {
        setOpenMoreInfo(true);
        setSelectedColaborador(dataFromRow);
    };

    const handleToggleAtivo = async (row: any) => {
        const payload = { ...row, ativo: !row.ativo };
        // Presume que updateColaboradorById recebe os dados no body
        try {
            await updateColaboradorById(token, row.id, payload);
            showToast("SUCCESS", "Status atualizado com sucesso", {});
            setLocalRefresh(!localRefresh); // Força recarregamento
        } catch (error) {
            showToast("ERROR", "Erro ao atualizar status", {});
        }
    };

    const handleDelete = async (row: any) => {
        if (window.confirm(`Tem certeza que deseja excluir o usuário ${row.nome}?`)) {
            try {
                await deleteColaboradorById(token, row.id);
                showToast("SUCCESS", "Usuário excluído com sucesso", {});
                setLocalRefresh(!localRefresh);
            } catch (error) {
                showToast("ERROR", "Erro ao excluir usuário", {});
            }
        }
    };

    // Definição Customizada de Colunas
    const customColumns = [
        {
            name: "Usuário",
            selector: (row: any) => row.nome,
            sortable: true,
            cell: (row: any) => (
                <div className="user-info-cell">
                    <div className="user-avatar">{row.nome.charAt(0).toUpperCase()}</div>
                    <div className="user-details">
                        <span className="user-name">{row.nome}</span>
                        <span className="user-email">{row.email}</span>
                    </div>
                </div>
            )
        },
        {
            name: "Função",
            selector: (row: any) => row.funcaoId,
            sortable: true,
            cell: (row: any) => {
                const func = dataAllFuncoes?.find((f: any) => f.id === row.funcaoId);
                return <span className="badge-light">{func ? func.nome : "..."}</span>;
            }
        },
        {
            name: "Expediente",
            selector: (row: any) => row.expedienteId,
            sortable: true,
            cell: (row: any) => {
                const exp = dataAllExpedientes?.find((e: any) => e.id === row.expedienteId);
                return <span className="badge-light">{exp ? exp.nome : "..."}</span>;
            }
        },
        {
            name: "Ativo",
            selector: (row: any) => row.ativo,
            sortable: true,
            center: true,
            width: "120px",
            cell: (row: any) => (
                <div className="toggle-cell" onClick={(e) => e.stopPropagation()}>
                    <label className="switch">
                        <input 
                            type="checkbox" 
                            checked={row.ativo} 
                            onChange={() => handleToggleAtivo(row)} 
                        />
                        <span className="slider round"></span>
                    </label>
                    <span className="toggle-label">{row.ativo ? 'Sim' : 'Não'}</span>
                </div>
            )
        },
        {
            name: "Ações",
            center: true,
            width: "120px",
            cell: (row: any) => (
                <div className="row-actions">
                    <button className="btn-action edit" onClick={(e) => { e.stopPropagation(); showMoreInfo(row); }} title="Editar">
                        <FaPen size={14} />
                    </button>
                    <button className="btn-action delete" onClick={(e) => { e.stopPropagation(); handleDelete(row); }} title="Excluir">
                        <FaTrash size={14} />
                    </button>
                </div>
            )
        }
    ];

    // Filtragem Local
    const filteredItems = dataAllColaboradores?.filter((item: any) => {
        const matchText = item.nome.toLowerCase().includes(filterText.toLowerCase()) || 
                          item.email.toLowerCase().includes(filterText.toLowerCase());
        
        let matchStatus = true;
        if (filterStatus === "active") matchStatus = item.ativo === true;
        if (filterStatus === "inactive") matchStatus = item.ativo === false;

        return matchText && matchStatus;
    });

    const activeUsersCount = dataAllColaboradores?.filter((c: any) => c.ativo).length || 0;

    return (
        <>
            <div className="usuarios__wrapper">
                <div className="usuarios__header">
                    <HeaderInside isHome={false} nome={"Usuários"} />
                </div>

                {/* Dashboard Stats Card */}
                <div className="usuarios__dashboard">
                    <div className="stat-card">
                        <div className="stat-icon users-icon">
                            👥
                        </div>
                        <div className="stat-info">
                            <h3>{dataAllColaboradores?.length || 0}</h3>
                            <p>Total de Usuários</p>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon active-icon">
                            ✨
                        </div>
                        <div className="stat-info">
                            <h3>{activeUsersCount}</h3>
                            <p>Usuários Ativos</p>
                        </div>
                    </div>
                    <div className="stat-actions">
                        <Link to="/dashboard/relatorios?id=1" className="btn-secondary" title="Exportar Relatório">
                            <BsDownload size={16} /> Exportar
                        </Link>
                        <button className="btn-primary" onClick={() => setaddModalOpen(true)}>
                            + Novo Usuário
                        </button>
                    </div>
                </div>

                {/* Filter Bar */}
                <div className="usuarios__filter-bar">
                    <div className="search-group">
                        <FaSearch className="icon" />
                        <input 
                            type="text" 
                            placeholder="Buscar por nome ou email..." 
                            value={filterText}
                            onChange={(e) => setFilterText(e.target.value)}
                        />
                    </div>
                    <div className="filter-group">
                        <FaFilter className="icon" />
                        <select 
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                        >
                            <option value="all">Todos os Status</option>
                            <option value="active">Apenas Ativos</option>
                            <option value="inactive">Apenas Inativos</option>
                        </select>
                    </div>
                </div>

                {/* Table Area */}
                {statusCodeAllColaboradores === 200 ? (
                    <div className="table__wrapper modern-table">
                        <DataTable
                            noHeader={true}
                            data={filteredItems || []}
                            columns={customColumns as any}
                            noDataComponent={<EmptyData hasMargin={true} />}
                            striped={false} // Remover listras antigas para visual mais clean
                            pagination={true}
                            onRowClicked={showMoreInfo}
                            pointerOnHover={true}
                            highlightOnHover={true}
                            customStyles={{
                                rows: {
                                    style: {
                                        minHeight: '72px', // Altura maior para acomodar avatar
                                    }
                                }
                            }}
                        />
                    </div>
                ) : (
                    <div className="animation__wrapper">
                        <Lottie
                            options={{ loop: true, animationData: LOADING }}
                            height={150} width={150}
                        />
                        <h2>Estamos carregando seus dados 🧐</h2>
                    </div>
                )}
            </div>

            {addModalOpen && (
                <ModalCrud onClose={closeModal}>
                    <AddSelectedColaborador />
                </ModalCrud>
            )}

            {openMoreInfo && (
                <ModalCrud onClose={closeModalMoreInfo}>
                    {selectedColaborador ? (
                        <SelectedColaborador data={selectedColaborador} />
                    ) : (
                        ""
                    )}
                </ModalCrud>
            )}
        </>
    );
};
export default Usuarios;
